import { Avatar, Menu, Typography } from 'antd';
import { TbBuilding, TbCircleCheckFilled, TbLogout, TbUserPlus, TbUsers } from 'react-icons/tb';

import { useAccountSessionManager } from '@/hooks/useAccountSessionManager';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import SmallTag from '@/shared/components/tags/SmallTag';
import { useMultiAccountStore } from '@/stores/auth/multiAccount';

import AccountActionsDropdown from './components/AccountActionsDropdown';
import AccountSessionFeedback from './components/AccountSessionFeedback';

interface AccountSwitcherItemsOptions {
    section?: 'all' | 'accounts' | 'actions';
    logoutScope?: 'all' | 'current';
    showStatus?: boolean;
    showMostRecent?: boolean;
    disabled?: boolean;
}

export default function useAccountSwitcherItems({
    section = 'all',
    logoutScope = 'all',
    showStatus = true,
    showMostRecent = false,
    disabled = false,
}: AccountSwitcherItemsOptions = {}) {
    const { token } = useAntDesignTheme();
    const { accounts, context, loading, switchingAccount, error, maxAccounts, mostRecentWorkspace } =
        useMultiAccountStore();
    const { switchAccount, addAccount, logoutAccounts, retry } = useAccountSessionManager();
    const busy = switchingAccount || loading || disabled;
    // Direct children let Menu register keys and keyboard navigation.
    return [
        showStatus && (loading || error || !accounts.length) && (
            <Menu.ItemGroup
                key='account-session-feedback'
                title={
                    <AccountSessionFeedback
                        disabled={busy}
                        loading={loading}
                        error={error}
                        onRetry={() => void retry()}
                    />
                }
            />
        ),
        ...(section !== 'actions' && !loading
            ? accounts.map((account) => (
                  <Menu.ItemGroup
                      key={account.id}
                      title={
                          <DropdownMenuLabel
                              icon={<Avatar size={16} src={account.avatar} icon={<TbUsers />} />}
                              extra={
                                  <AccountActionsDropdown
                                      accountId={account.id}
                                      emailAddress={account.emailAddress}
                                      disabled={busy}
                                  />
                              }
                              style={{ padding: 0 }}
                          >
                              <Typography.Text
                                  ellipsis
                                  style={{ maxWidth: '16rem', fontSize: 'inherit', color: 'inherit' }}
                              >
                                  {account.emailAddress}
                              </Typography.Text>
                          </DropdownMenuLabel>
                      }
                  >
                      {account.workspaces.length ? (
                          account.workspaces.map((workspace) => {
                              const active = context?.accountId === account.id && context.workspaceId === workspace.id;
                              return (
                                  <Menu.Item
                                      key={`account:${account.id}:workspace:${workspace.id}`}
                                      disabled={busy || active}
                                      style={{
                                          height: 'auto',
                                          padding: token.paddingXS,
                                          margin: 0,
                                          width: 'auto',
                                          lineHeight: 'normal',
                                      }}
                                      onClick={() => void switchAccount(account.id, workspace.id)}
                                  >
                                      <DropdownOptionContent
                                          avatar={{
                                              src: workspace.avatar,
                                              icon: workspace.emoji || <TbBuilding />,
                                              props: { size: 32 },
                                          }}
                                          avatarStyle={{ marginBlock: token.marginXXS }}
                                          description={account.plan ?? '—'}
                                          extraAlign='center'
                                          extra={
                                              showMostRecent &&
                                              mostRecentWorkspace?.accountId === account.id &&
                                              mostRecentWorkspace.workspaceId === workspace.id ? (
                                                  <SmallTag
                                                      compact
                                                      message='Most Recent'
                                                      styles={{ marginLeft: 0, color: token.colorTextTertiary }}
                                                  />
                                              ) : undefined
                                          }
                                          singleLine
                                          title={
                                              <>
                                                  <span style={{ color: token.colorText }}>{workspace.name}</span>
                                                  {active && (
                                                      <TbCircleCheckFilled
                                                          aria-label='Active workspace'
                                                          style={{
                                                              marginLeft: '0.5rem',
                                                              verticalAlign: '-0.125rem',
                                                              color: token.colorPrimary,
                                                          }}
                                                      />
                                                  )}
                                              </>
                                          }
                                      />
                                  </Menu.Item>
                              );
                          })
                      ) : (
                          <Menu.ItemGroup
                              key={`account:${account.id}:empty`}
                              title={
                                  <EmptyView
                                      icon={<TbBuilding />}
                                      height='9rem'
                                      label='No Workspaces'
                                      description='Continue to join or create a workspace.'
                                  />
                              }
                          >
                              <Menu.Item
                                  key={`account:${account.id}`}
                                  disabled={busy || account.id === context?.accountId}
                                  onClick={() => void switchAccount(account.id)}
                              >
                                  Continue as {account.firstName || account.emailAddress}
                              </Menu.Item>
                          </Menu.ItemGroup>
                      )}
                  </Menu.ItemGroup>
              ))
            : []),
        section === 'all' && context && <Menu.Divider key='account-actions-divider' />,
        section !== 'accounts' && context && (
            <Menu.Item
                key='add-account'
                icon={<TbUserPlus size='1rem' />}
                disabled={busy || accounts.length >= maxAccounts}
                onClick={() => void addAccount()}
            >
                {accounts.length >= maxAccounts ? `Account limit reached (${maxAccounts})` : 'Add Account'}
            </Menu.Item>
        ),
        section !== 'accounts' && (logoutScope === 'current' ? Boolean(context?.accountId) : accounts.length > 0) && (
            <Menu.Item
                key={logoutScope === 'current' ? 'logout-current-account' : 'logout-all-accounts'}
                icon={<TbLogout size='1rem' />}
                disabled={busy}
                onClick={() => {
                    if (logoutScope === 'current') {
                        if (context?.accountId) {
                            void logoutAccounts([context.accountId]);
                        }
                        return;
                    }
                    void logoutAccounts();
                }}
            >
                {logoutScope === 'current' ? 'Sign Out of Account' : 'Sign Out of All Accounts'}
            </Menu.Item>
        ),
    ];
}
